import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {getLabwareDataById} from 'stores/selectors/labwareStateSelectors';
import {DirectionType, Feedback, IFCPlateVariant, PipettingParameterFieldKey} from 'constants/dataEnum';
import {isTaskTipCountValid} from 'lib/cardAndTaskHelper';
import {useMemo} from 'react';
import {getAllowedTipTouchOffDirectionChange, getMaxWellTipPositionValue} from 'lib/PipettingProfileItemHelper';
import {showFailedToUpdatePipettingValueModal} from 'stores/business/commonTaskListBusiness';
import {showSnackbar} from 'stores/actions/appStateAction';
import {resolveIntl} from 'stores/business/commonBusiness';
import {getMaximumTouchOffTolerance} from 'stores/selectors/configSelectors';
import {PipettingProfileItemWrapper} from '../PipettingProfileWrapper';
import {parseMessage} from '../../../../../lib/stringHelper';
import PipettingProfileInputType from '../PipettingProfileInputType';
import PipettingItemTipTouchDirection from './PipettingItemTipTouchDirection';

function PipettingItemTipTouchOff({
  task,
  onChangeSwitch,
  onChangeDelay,
  onChangeHeight,
  onChangeDirectionX,
  onChangeDirectionY,
}) {
  const {
    tipTouchOff,
    tipTouchOffDelay,
    labwareID,
    tipTouchOffHeight,
    tipTouchOffDirectionX,
    tipTouchOffDirectionY,
    type,
  } = task || {};
  const dispatch = useDispatch();
  const intl = resolveIntl();
  const labware = useSelector(getLabwareDataById(labwareID));
  const directionLimitTolerance = useSelector(getMaximumTouchOffTolerance);
  const isAllowEnabledTipTouch = useMemo(() => isTaskTipCountValid(task, labware), [task, labware]);

  const labelSwitch = parseMessage('protocolEditor.taskList.pipettingProfile.tipTouchOff.switch', {
    taskType: type.toLowerCase(),
  });
  const labelDelay = parseMessage('protocolEditor.taskList.pipettingProfile.tipTouchOff.delayName');
  const labelHeight = parseMessage('protocolEditor.taskList.pipettingProfile.tipTouchOff.heightPosition');
  const isAllowedTipTouchOffDirectionChange = useMemo(() => getAllowedTipTouchOffDirectionChange(labware), [labware]);

  const isTipTouchOfDisabled = () => {
    const {isIFC, rowCount} = labware || {};

    return isIFC && (rowCount === IFCPlateVariant.IFC48 || rowCount === IFCPlateVariant.IFC96);
  };

  const directionValueLimit = useMemo(() => getMaxWellTipPositionValue(labware), [labware]);
  const maxValueOffsetX = directionValueLimit.x + (directionLimitTolerance || 0);
  const maxValueOffsetY = directionValueLimit.y + (directionLimitTolerance || 0);

  const tipTouchOffDelayClassName = useMemo(() => {
    if (!isAllowEnabledTipTouch) return 'disabled';

    if (!tipTouchOff) return 'disabled';
    return '';
  }, [isAllowEnabledTipTouch, tipTouchOff]);

  const onOverValueHandler = (isOverValue) => {
    if (isOverValue) {
      dispatch(
        showSnackbar(
          Feedback.WARNING,
          intl.formatMessage({
            id: 'protocolEditor.taskList.pipettingProfile.tipTouchOff.error.overValue',
          }),
        ),
      );
    }
  };

  return (
    <div className={`pipetting-profile__item__tip-touch-off-container${isTipTouchOfDisabled() ? 'disabled' : ''}`}>
      <PipettingProfileItemWrapper>
        {labelSwitch}
        <PipettingProfileInputType.Switch
          checked={!isAllowEnabledTipTouch ? false : tipTouchOff}
          onChange={(value) => {
            if (!isTaskTipCountValid(task, labware)) {
              dispatch(showFailedToUpdatePipettingValueModal(PipettingParameterFieldKey.TIP_TOUCH_OFF));
              return;
            }
            onChangeSwitch(value, type);
          }}
        />
      </PipettingProfileItemWrapper>
      <div className={`pipetting-profile__item__tip-touch-off-delay ${tipTouchOffDelayClassName}`}>
        <PipettingProfileItemWrapper>
          <li>{labelDelay}</li>
          <PipettingProfileInputType.NumericTextBox
            value={tipTouchOffDelay}
            onChange={(value) => onChangeDelay(value, type)}
            minValue={0}
          />
          <PipettingProfileInputType.Unit.SecondAfterTouch />
          <li>{labelHeight}</li>
          <PipettingProfileInputType.NumericTextBox
            value={tipTouchOffHeight > labware.wellDepth ? onChangeHeight(labware.wellDepth, type) : tipTouchOffHeight}
            maxValue={labware.wellDepth}
            minValue={0}
            onChange={(value) => onChangeHeight(value, type)}
            onOverValue={onOverValueHandler}
          />
          <PipettingProfileInputType.Unit.MillimeterAboveBottomWell />
          {isAllowedTipTouchOffDirectionChange.x && (
            <>
              <PipettingItemTipTouchDirection
                labware={labware}
                position={DirectionType.DIRECTION_X}
                maxValueOffset={maxValueOffsetX}
                value={
                  tipTouchOffDirectionX > maxValueOffsetX
                    ? onChangeDirectionX(maxValueOffsetX, type)
                    : tipTouchOffDirectionX
                }
                onChangeDirection={(value) => onChangeDirectionX(value, type)}
                onOverValue={onOverValueHandler}
              />
              <PipettingProfileInputType.Unit.MillimeterFromCenterWell />
            </>
          )}
          {isAllowedTipTouchOffDirectionChange.y && (
            <>
              <PipettingItemTipTouchDirection
                labware={labware}
                position={DirectionType.DIRECTION_Y}
                maxValueOffset={maxValueOffsetY}
                value={
                  tipTouchOffDirectionY > maxValueOffsetY
                    ? onChangeDirectionY(maxValueOffsetY, type)
                    : tipTouchOffDirectionY
                }
                onChangeDirection={(value) => onChangeDirectionY(value, type)}
                onOverValue={onOverValueHandler}
              />
              <PipettingProfileInputType.Unit.MillimeterFromCenterWell />
            </>
          )}
        </PipettingProfileItemWrapper>
      </div>
    </div>
  );
}

PipettingItemTipTouchOff.propTypes = {
  onChangeSwitch: PropTypes.func.isRequired,
  onChangeDelay: PropTypes.func.isRequired,
  onChangeHeight: PropTypes.func.isRequired,
  onChangeDirectionX: PropTypes.func.isRequired,
  onChangeDirectionY: PropTypes.func.isRequired,
  task: PropTypes.object.isRequired,
};

export default PipettingItemTipTouchOff;
